import React, { useCallback, useEffect, useState, useRef } from "react";
import AccountLayout from "../../blocs/AccountLayout";
import { useDispatch, useSelector } from "react-redux";
import { EstablishmentForm } from "../../components/etablissement/infoEtablissement";
import {
  createEtablissementPhotos,
  deleteEtablissementPhoto,
  getMyEtablissement,
  updatedEtablissement,
  updateEtablissementPhotos,
} from "../../store/slices/etablissementSlice";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { validateFrenchIBAN } from "../../utils/utils";
import { Formik, useFormikContext } from "formik";
import { ActivityTypeForm } from "../../components/etablissement/type";
import { fetchCategories } from "../../store/slices/categorySlice";
import { OpeningHoursForm } from "../../components/etablissement/horaires";
import { PhotoUploadForm } from "../../components/etablissement/photo";
import ServicesPrestataire from "../../components/etablissement/servicesPresta";
import EquipeEtablissement from "../../components/etablissement/equipesEtablissement";

const MAX_FILES = 10;
const MAX_SIZE_MB = 2;
const ALLOWED_FORMATS = ["image/jpeg", "image/png"];

function PageEtablissement() {
  const [schedules, setSchedules] = useState([]);
  const [members, setMembers] = useState([{ id: 1, name: "" }]);
  const [photos, setPhotos] = useState([]);
  const [errorPhotos, setErrorPhotos] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const dispatch = useDispatch();
  const { selectedEtablissement, isLoading, error } = useSelector(
    (state) => state.etablissements
  );
  
  // Référence pour suivre les photos
  const photosRef = useRef(photos);
  
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  const [initialValues, setInitialValues] = useState({
    nom: "",
    description: "",
    iban: "",
    companyAddress: "",
    lat: null,
    lng: null,
    postalCode: null,
    city: null,
    siret: "",
    tva: "",
    cinRecto: "",
    cinVerso: ""
  });

  // Validation schema
  const validationSchema = Yup.object().shape({
    description: Yup.string()
      .max(1000, "La description ne doit pas dépasser 1000 caractères"),
    nom: Yup.string().max(50, "Le nom de l'entreprise ne doit pas dépasser 50 caractères"),
  });

  const { categories } = useSelector((state) => state.categories);

  useEffect(() => {
    dispatch(getMyEtablissement());
    dispatch(fetchCategories());
  }, [dispatch]);
 
  const transformHorairesToSchedules = (horaires) => {
    const scheduleGroups = {};

    horaires?.forEach((horaire) => {
      const key = `${horaire.horaire_ouverture}-${horaire.horaire_fermeture}-${horaire.pause_debut}-${horaire.pause_fin}-${horaire.continuous}-${horaire.fermee}`;

      if (!scheduleGroups[key]) {
        scheduleGroups[key] = {
          days: [],
          opening: horaire.horaire_ouverture
            ? horaire.horaire_ouverture.substring(0, 5)
            : null,
          closing: horaire.horaire_fermeture
            ? horaire.horaire_fermeture.substring(0, 5)
            : null,
          pauseStart: horaire.pause_debut
            ? horaire.pause_debut.substring(0, 5)
            : null,
          pauseEnd: horaire.pause_fin
            ? horaire.pause_fin.substring(0, 5)
            : null,
          continuous: Boolean(horaire.continuous),
          closedAllDay: Boolean(horaire.fermee),
        };
      }

      const day = horaire.jour.charAt(0).toUpperCase() + horaire.jour.slice(1);
      scheduleGroups[key].days.push(day);
    });

    return Object.values(scheduleGroups).map((group, index) => ({
      id: index + 1,
      ...group,
    }));
  };

  // Update initial values when establishment data is loaded
  useEffect(() => {
    if (selectedEtablissement) {
      // Trouver les documents d'identité
      const identityDocs = selectedEtablissement?.user_verifications || [];
      const cinRectoDoc = identityDocs.find(doc => doc.document_type === "identity_document");
      const cinVersoDoc = identityDocs.find(doc => doc.document_type === "identity_document_back");
      
      setInitialValues({
        nom: selectedEtablissement?.nom || "",
        description: selectedEtablissement?.description || "",
        iban: selectedEtablissement?.iban || "",
        companyAddress: selectedEtablissement?.adresse_postale || "",
        lat: selectedEtablissement?.lat || null,
        lng: selectedEtablissement?.lng || null,
        postalCode: selectedEtablissement?.code_postal || null,
        city: selectedEtablissement?.ville || null,
        immediat: selectedEtablissement?.immediat === 0 ? false : true,
        domaine: selectedEtablissement?.domaine_activite
          ? parseInt(selectedEtablissement.domaine_activite)
          : "",
        type: selectedEtablissement?.type
          ? parseInt(selectedEtablissement.type)
          : "",
        activity: selectedEtablissement?.activite
          ? parseInt(selectedEtablissement.activite)
          : "",
        lieux: selectedEtablissement?.lieu_intervention || [],
        siret: selectedEtablissement?.user?.siret || "",
        tva: selectedEtablissement?.user?.tva || "",
        // cinRecto: cinRectoDoc?.file_url || "",
        // cinVerso: cinVersoDoc?.file_url || ""
      });
      
      setSchedules(
        transformHorairesToSchedules(selectedEtablissement?.horaires)
      );
      
      // Initialiser les photos
      if (selectedEtablissement?.photos) {
        setPhotos(selectedEtablissement.photos.map(photo => ({
          ...photo,
          id: photo.id || `existing-${photo.url_photo}`
        })));
      }
    }
  }, [selectedEtablissement]);

  const handleSave = (updatedSchedules) => {
    setSchedules(updatedSchedules);
  };

  const handleSubmit = async (values) => {
    try {
      const cleanedIban = values?.iban?.replace(/\s+/g, "");
      
      const data = {
        iban: cleanedIban,
        nom: values?.nom,
        lat: values.lat?.toString(),
        description: values?.description,
        lng: values.lng?.toString(),
        adresse_postale: values.companyAddress,
        code_postal: values.postalCode,
        ville: values.city,
        activite: values?.activity,
        domaine_activite: values?.domaine,
        type: values?.type,
        lieu_intervention: values?.lieux,
        immediat: values.immediat,
        siret: values.siret,
        tva: values.tva,
        cinRecto:values.cinRecto
      };

      if (selectedEtablissement) {
        await dispatch(
          updatedEtablissement({
            etablissementData: data,
            route: `etablissements/${selectedEtablissement?.id}/first-update`,
          })
        ).unwrap();
        toast.success(
          "Informations de l'établissement mises à jour avec succès"
        );
        dispatch(getMyEtablissement());
      }
    } catch (error) {
      toast.error(
        error?.data?.error || "Erreur lors de la mise à jour des informations"
      );
    }
  };
 // Trouver les documents d'identité
//  const identityDocs = selectedEtablissement?.user_verifications || [];
//  const cinRectoDoc = identityDocs.find(doc => doc.document_type === "identity_document");
//  const cinVersoDoc = identityDocs.find(doc => doc.document_type === "identity_document_back");
  // Get initial values from selectedEtablissement or use defaults
  const getInitialValues = () => ({
    nom: selectedEtablissement?.nom || "",
    siret: selectedEtablissement?.user?.siret || "",
    tva: selectedEtablissement?.user?.tva || "",
    // cinRecto: cinRectoDoc?.file_url || "",
    // cinVerso: cinVersoDoc?.file_url || "",
    description: selectedEtablissement?.description || "",
    iban: selectedEtablissement?.iban || "",
    companyAddress: selectedEtablissement?.adresse_postale || "",
    lat: selectedEtablissement?.lat || null,
    lng: selectedEtablissement?.lng || null,
    postalCode: selectedEtablissement?.code_postal || null,
    city: selectedEtablissement?.ville || null,
    immediat: selectedEtablissement?.immediat === 0 ? false : true,
    domaine: selectedEtablissement?.domaine_activite
      ? parseInt(selectedEtablissement.domaine_activite)
      : "",
    type: selectedEtablissement?.type
      ? parseInt(selectedEtablissement.type)
      : "",
    activity: selectedEtablissement?.activite
      ? parseInt(selectedEtablissement.activite)
      : "",
    lieux: selectedEtablissement?.lieu_intervention || [],
  });

  const isValidImageFile = useCallback((file) => {
    if (!file || !file.type) return false;
    
    const mimeType = file.type.toLowerCase();
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    return (
      ALLOWED_FORMATS.includes(mimeType) ||
      (mimeType.startsWith('image/') && ['jpeg', 'jpg', 'png'].includes(fileExtension))
    );
  }, []);

  // Helper function to get image dimensions
  const getImageDimensions = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = function() {
        resolve({ width: this.width, height: this.height });
        URL.revokeObjectURL(this.src);
      };
      img.onerror = function() {
        reject(new Error('Failed to load image'));
        URL.revokeObjectURL(this.src);
      };
      img.src = URL.createObjectURL(file);
    });
  }, []);

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    let newError = "";
    setIsProcessing(true);
    
    // Créer un tableau pour stocker les nouvelles photos
    const newPhotos = [];
    
    // Utiliser la valeur actuelle des photos via la ref
    const currentPhotos = photosRef.current;
    
    // Process files sequentially
    for (const file of files) {
      // Check if we've already reached max files
      if (currentPhotos.length + newPhotos.length >= MAX_FILES) {
        newError = `Vous ne pouvez ajouter que ${MAX_FILES} photos au maximum.`;
        break;
      }

      if (!isValidImageFile(file)) {
        newError = `Le fichier ${file.name} n'est pas un JPG/PNG valide`;
        continue;
      }

      // Check file size
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        newError = "La taille maximale par image est de 2 Mo.";
        continue;
      }

      // Check image dimensions
      try {
        const dimensions = await getImageDimensions(file);
        if (dimensions.width > 1920 || dimensions.height > 1080) {
          newError = "Les dimensions maximales sont 1920x1080 px.";
          continue;
        }
      } catch (error) {
        newError = "Impossible de lire les dimensions de l'image.";
        continue;
      }

      // If all checks pass, add to newPhotos array
      const newPhoto = {
        id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        preview: URL.createObjectURL(file),
        file: file,
        order: currentPhotos.length + newPhotos.length + 1,
      };
      
      newPhotos.push(newPhoto);
    }

    // Mettre à jour l'état une seule fois avec toutes les nouvelles photos
    if (newPhotos.length > 0) {
      setPhotos(prevPhotos => [...prevPhotos, ...newPhotos]);
    }

    setErrorPhotos(newError);
    setIsProcessing(false);
    
    // Clear the input to allow selecting the same file again
    event.target.value = '';
  };

  const handleDelete = (data) => {
    if (data.url_photo) {
      dispatch(deleteEtablissementPhoto(data.id));
    }

    const newPhotos = photos
      .filter((photo) => photo.id !== data?.id)
      .map((photo, index) => ({
        ...photo,
        order: index + 1, // Reindex remaining photos
      }));
    setPhotos(newPhotos);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(photos);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property based on new position
    const reorderedPhotos = items.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    setPhotos(reorderedPhotos);
  };

  const handleSubmitPhotos = async () => {
    if (photos.length === 0) {
      setErrorPhotos("Veuillez ajouter au moins une photo.");
      return;
    }
    
    // Create FormData with ordered photos
    const formData = new FormData();
    const updates = [];
    
    photos.forEach((photo, index) => {
      if (photo.url_photo) {
        // Existing photo - just track order changes
        updates.push({
          id: photo.id,
          sort: photo.order, // Update order based on current position
        });
      } else {
        formData.append(`new_photos[]`, photo.file);
        formData.append(`new_photos_sort[]`, photo.order);
      }
    });
    
    if (updates.length > 0) {
      formData.append(`updates_json`, JSON.stringify(updates));
    }
    
    try {
      await dispatch(updateEtablissementPhotos(formData)).unwrap();
      toast.success("Photos mises à jour avec succès");
      dispatch(getMyEtablissement());
    } catch (error) {
      toast.error("Erreur lors de la mise à jour des photos");
    }
  };

  return (
    <AccountLayout bgColor="gray-Bg bgShadow">
      <h3 className="pd-bt-28">Ma page d'établissement</h3>
      <p className="body grey66">
        Vous pouvez modifier ici toutes les informations concernant votre
        établissement et vos prestations. Attention, toutes ces informations
        sont visibles sur la page publique de votre établissement où les
        utilisateurs Twyny prendront rendez-vous.
      </p>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <>
            <div className="card-twy mt-28">
              <EstablishmentForm
                from="account"
                selectedEtablissement={selectedEtablissement}
                isSubmitting={isSubmitting}
                values={values}
                setFieldValue={setFieldValue}
                userVerifications={selectedEtablissement?.user_verifications || []}
              />
            </div>
            <div className="card-twy mt-28">
              <ActivityTypeForm
                from="account"
                categories={categories}
                selectedEtablissement={selectedEtablissement}
                errors={errors}
                touched={touched}
              />
            </div>
            <div className="card-twy mt-28">
              <OpeningHoursForm
                initialSchedules={schedules}
                selectedEtablissement={selectedEtablissement}
                onSave={handleSave}
                from="account"
                days={[
                  "Lundi",
                  "Mardi",
                  "Mercredi",
                  "Jeudi",
                  "Vendredi",
                  "Samedi",
                  "Dimanche",
                ]}
              />
            </div>
            <div className="card-twy mt-28">
              <PhotoUploadForm
                selectedEtablissement={selectedEtablissement}
                photos={photos}
                maxFiles={MAX_FILES}
                maxSizeMB={MAX_SIZE_MB}
                allowedFormats={ALLOWED_FORMATS}
                onFileChange={handleFileChange}
                onDelete={handleDelete}
                onDragEnd={handleDragEnd}
                error={errorPhotos}
                handleSubmit={handleSubmitPhotos}
                from="account"
                isLoading={isProcessing}
              />
            </div>
            <div className="card-twy mt-28">
              <ServicesPrestataire
                from="account"
                selectedEtablissement={selectedEtablissement}
              />
            </div>
            <div className="card-twy mt-28">
              <h4 className="grey500"></h4>
              <EquipeEtablissement
                from="account"
                members={members}
                setMembers={setMembers}
              />
            </div>
          </>
        )}
      </Formik>
    </AccountLayout>
  );
}

export default PageEtablissement;